import { z } from 'zod';
/**
 * Envios em lote (T7.41, estudo aprovado em 26/10). A mesma mensagem, com o nome de cada um,
 * para muita gente. Pelo app do Secretário sai direto do servidor; pelo WhatsApp sai pelo
 * `wa.me` (a pessoa toca em enviar lá), de 50 em 50, com 60 s entre uma conversa aberta e a
 * outra, contados no servidor por negócio. O servidor é o dono do envio: a tela só mostra.
 */
export const OUTREACH_WA_BATCH = 50;
export const OUTREACH_WA_GAP_SECONDS = 60;
/** Teto de conversas do WhatsApp abertas por dia, por negócio. */
export const OUTREACH_WA_DAILY = 150;
/** Pessoas por envio pelo app (fase 1; sobe depois da medição no servidor). */
export const OUTREACH_APP_MAX = 2000;
/** Para quem recebe pelo app: envios em lote do mesmo negócio por dia e por semana. */
export const OUTREACH_APP_PER_DAY = 1;
export const OUTREACH_APP_PER_WEEK = 3;
export const OUTREACH_TEXT_MAX = 700;
/** Onde o texto leva o primeiro nome de cada pessoa. */
export const OUTREACH_NAME_TOKEN = '{nome}';
export const OUTREACH_SCOPES = ['clinica', 'loja', 'pessoal'];
export const OUTREACH_STATES = ['enviando', 'pausado', 'concluido', 'cancelado'];
export const OUTREACH_CHANNELS = ['app', 'whatsapp'];
/**
 * fila: esperando; aberto: o WhatsApp da pessoa foi aberto e falta saber se mandou;
 * enviado: no app, entregue; no WhatsApp, você disse que mandou (ou abriu o próximo);
 * pulado: você disse que não mandou; tirado: você tirou da fila; falhou: não deu para entregar.
 */
export const OUTREACH_ITEM_STATES = [
    'fila',
    'aberto',
    'enviado',
    'pulado',
    'tirado',
    'falhou',
];
export const OUTREACH_ITEM_DONE = [
    'enviado',
    'pulado',
    'tirado',
    'falhou',
];
export const outreachItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    channel: z.enum(OUTREACH_CHANNELS),
    /** O lote do WhatsApp (1, 2, …); 0 no app. */
    lot: z.number().int(),
    state: z.enum(OUTREACH_ITEM_STATES),
    /** Telefone para mostrar na mesa (só WhatsApp). */
    phone: z.string().nullable(),
    /** Por que ficou de fora ou para depois ("Já recebeu um envio seu hoje"). */
    reason: z.string().nullable(),
    /** No app: não sai antes disto (limite de quem recebe). */
    notBefore: z.iso.datetime().nullable(),
    at: z.iso.datetime().nullable(),
});
export const outreachCountsSchema = z.object({
    total: z.number().int(),
    app: z.number().int(),
    appSent: z.number().int(),
    whatsapp: z.number().int(),
    whatsappSent: z.number().int(),
    skipped: z.number().int(),
    removed: z.number().int(),
    failed: z.number().int(),
});
export const outreachBatchSchema = z.object({
    id: z.string(),
    scope: z.enum(OUTREACH_SCOPES),
    title: z.string(),
    /** O texto-modelo, com {nome}. */
    text: z.string(),
    state: z.enum(OUTREACH_STATES),
    counts: outreachCountsSchema,
    /** O lote do WhatsApp em andamento e quantos lotes há. */
    lot: z.number().int(),
    lots: z.number().int(),
    /** Quem abre a seguir no WhatsApp, e quando pode (hora do servidor). */
    next: z
        .object({
        itemId: z.string(),
        name: z.string(),
        readyAt: z.iso.datetime(),
        /** Por que não abre agora: a espera de 60 s, o teto do dia ou o lote no fim. */
        hold: z.enum(['espera', 'teto_do_dia', 'nenhum']),
    })
        .nullable(),
    /** O WhatsApp aberto por último, que falta dizer se mandou. */
    open: z.object({ itemId: z.string(), name: z.string() }).nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
export const outreachDetailSchema = outreachBatchSchema.extend({
    items: z.array(outreachItemSchema),
});
/** Abrir o próximo WhatsApp: e o anterior, mandou? (padrão: sim). */
export const outreachOpenSchema = z.object({
    previous: z.enum(['enviado', 'pulado']).optional(),
});
export const outreachOpenResultSchema = z.object({
    itemId: z.string(),
    name: z.string(),
    link: z.string(),
    batch: outreachBatchSchema,
});
export const outreachMarkSchema = z.object({ state: z.enum(['enviado', 'pulado']) });
/** O texto com o primeiro nome da pessoa no lugar de {nome}. */
export function outreachTextFor(template, fullName) {
    const first = fullName.trim().split(/\s+/)[0] ?? '';
    return template
        .split(OUTREACH_NAME_TOKEN)
        .join(first)
        .replace(/\s+([,.!?])/g, '$1')
        .trim();
}
